import {
  EElement,
  EText,
  ERecord,
  EList,
  EEnum,
} from '@packages/systems/wfdl-ast';
import {
  DEFAULT_LAYOUT_COL_STYLE_VALUE,
  DEFAULT_LAYOUT_ROW_STYLE_VALUE,
} from './constants';
import {wrapAddPanelIcon} from '@packages/systems/ui/shared-ui/wrapAddPanelIcon';
import {
  AddPanelContainer64Icon,
  AddPanelRow64Icon,
  AddPanelSection64Icon,
  AddPanelGrid64Icon,
  AddPanelQuickStack64Icon,
  AddPanelHFlexIcon,
  AddPanelVFlexIcon,
  GridIcon as DisplayGridIcon,
  ElementSectionIcon as SectionIcon,
  ElementRowIcon as RowIcon,
  ElementColumnIcon as ColumnIcon,
  ElementContainerIcon as ContainerIcon,
  ElementQuickStackIcon as LayoutIcon,
  ElementQuickStackCellIcon as CellIcon,
} from '@packages/systems/spring';

import Container from './Container';
import Row from './Row';
import Column from './Column';
import Section from './Section';
import Grid from './Grid';
import Layout from './Layout';
import Cell from './Cell';
import BlockContainer from './BlockContainer';
import VFlex from './Flex/VFlex';
import HFlex from './Flex/HFlex';

import AddPanelLayoutIcon from './icons/addpanel-layout';
import {Plugins, ElementPreset, Component} from '@packages/systems/wfdl';
import {qualify, iconName} from '@packages/systems/wfdl-Qualified';

const BlockContainerAddPanelIcon = wrapAddPanelIcon(AddPanelContainer64Icon);
const ContainerAddPanelIcon = wrapAddPanelIcon(AddPanelContainer64Icon);
const GridAddPanelIcon = wrapAddPanelIcon(AddPanelGrid64Icon);
const HFlexAddPanelIcon = wrapAddPanelIcon(AddPanelHFlexIcon);
const NewSectionAddPanelIcon = wrapAddPanelIcon(AddPanelSection64Icon);
const QuickStackAddPanelIcon = wrapAddPanelIcon(AddPanelQuickStack64Icon);
const RowAddPanelIcon = wrapAddPanelIcon(AddPanelRow64Icon);
const SectionAddPanelIcon = wrapAddPanelIcon(AddPanelSection64Icon);
const VFlexAddPanelIcon = wrapAddPanelIcon(AddPanelVFlexIcon);

export const plugin = Plugins.PrivilegedPlugin({
  icons: {
    // @ts-expect-error - TS2322 - Type 'ForwardRefExoticComponent<Omit<any, "ref"> & RefAttributes<any>>' is not assignable to type 'ComponentType<IconProps>'.
    AddPanelContainer: ContainerAddPanelIcon,
    Container: ContainerIcon,
    Column: ColumnIcon,
    Section: SectionIcon,
    // @ts-expect-error - TS2322 - Type 'ForwardRefExoticComponent<Omit<any, "ref"> & RefAttributes<any>>' is not assignable to type 'ComponentType<IconProps>'.
    AddPanelSection: SectionAddPanelIcon,
    DisplayGrid: DisplayGridIcon,
    // @ts-expect-error - TS2322 - Type 'ForwardRefExoticComponent<Omit<any, "ref"> & RefAttributes<any>>' is not assignable to type 'ComponentType<IconProps>'.
    Grid: GridAddPanelIcon,
    Layout: LayoutIcon,
    // @ts-expect-error - TS2322 - Type 'ForwardRefExoticComponent<Omit<any, "ref"> & RefAttributes<any>>' is not assignable to type 'ComponentType<IconProps>'.
    Layout2Plus1: QuickStackAddPanelIcon,
    AddPanelLayout: AddPanelLayoutIcon,
    Cell: CellIcon,
    Row: RowIcon,
    // @ts-expect-error - TS2322 - Type 'ForwardRefExoticComponent<Omit<any, "ref"> & RefAttributes<any>>' is not assignable to type 'ComponentType<IconProps>'.
    AddPanelRow: RowAddPanelIcon,
    // @ts-expect-error - TS2322 - Type 'ForwardRefExoticComponent<Omit<any, "ref"> & RefAttributes<any>>' is not assignable to type 'ComponentType<IconProps>'.
    BlockContainer: BlockContainerAddPanelIcon,
    // @ts-expect-error - TS2322 - Type 'ForwardRefExoticComponent<Omit<any, "ref"> & RefAttributes<any>>' is not assignable to type 'ComponentType<IconProps>'.
    VFlex: VFlexAddPanelIcon,
    // @ts-expect-error - TS2322 - Type 'ForwardRefExoticComponent<Omit<any, "ref"> & RefAttributes<any>>' is not assignable to type 'ComponentType<IconProps>'.
    HFlex: HFlexAddPanelIcon,
    // @ts-expect-error - TS2322 - Type 'ForwardRefExoticComponent<Omit<any, "ref"> & RefAttributes<any>>' is not assignable to type 'ComponentType<IconProps>'.
    NewSection: NewSectionAddPanelIcon,
  },
  atoms: {
    Container,
    Row,
    Column,
    Section,
    Grid,
    Layout,
    Cell,
    BlockContainer,
    VFlex,
    HFlex,
  },
  elementPresets: {
    Section: ElementPreset.create({
      addTabIndex: 1,
      category: null,
      icon: {
        small: qualify(Plugins.name('Layout'), iconName('NewSection')),
        medium: qualify(Plugins.name('Layout'), iconName('NewSection')),
      },
      label: 'Section',
      factory: EElement({
        id: 'Section',
        type: qualify(Plugins.name('Layout'), Component.name('Section')),
        data: ERecord({
          grid: ERecord({
            type: EText('section'),
          }),
          tag: EEnum('section'),
          children: EList([]),
        }),
      }),
      helpData: {
        tag: 'div',
        description: [
          'A Section is a Div block that extends across the full width of a browser window. This is perfect for full-screen background images and videos.',
        ],
        tip: 'To center Section content, drop a Container inside of the Section, then add your image or video to the Container. Voila!',
        videoSrc:
          'https://d3e54v103j8qbb.cloudfront.net/videos/tooltips/d2/section.57d78c48f6.mp4',
      },
    }),
    BlockContainer: ElementPreset.create({
      addTabIndex: 2,
      category: null,
      icon: {
        small: qualify(Plugins.name('Layout'), iconName('BlockContainer')),
        medium: qualify(Plugins.name('Layout'), iconName('BlockContainer')),
      },
      label: 'Container',
      factory: EElement({
        id: 'BlockContainer',
        type: qualify(Plugins.name('Layout'), Component.name('BlockContainer')),
        data: ERecord({
          grid: ERecord({
            type: EText('container'),
          }),
          children: EList([]),
          tag: EEnum('div'),
        }),
      }),
      helpData: {
        tag: 'div',
        description: [
          'A Container is a Div block with predefined styles to keep your content centered. On bigger screens, containers default to 940 pixels. On smaller devices, they fit the full width of the screen.',
        ],
        videoSrc:
          'https://d3e54v103j8qbb.cloudfront.net/videos/tooltips/d2/container.b453b838f0.mp4',
      },
    }),
    QuickStack: ElementPreset.create({
      addTabIndex: 3,
      category: 'Structure',
      icon: {
        small: qualify(Plugins.name('Layout'), iconName('Layout2Plus1')),
        medium: qualify(Plugins.name('Layout'), iconName('Layout2Plus1')),
      },
      label: 'Quick Stack',
      factory: EElement({
        id: 'QuickStack',
        type: qualify(Plugins.name('Layout'), Component.name('Layout')),
        data: ERecord({
          style: ERecord({
            base: ERecord({
              main: ERecord({
                noPseudo: ERecord({
                  gridTemplateColumns: EText(
                    `${DEFAULT_LAYOUT_COL_STYLE_VALUE} ${DEFAULT_LAYOUT_COL_STYLE_VALUE}`
                  ),
                  gridTemplateRows: EText(DEFAULT_LAYOUT_ROW_STYLE_VALUE),
                }),
              }),
            }),
          }),
          children: EList([
            EElement({
              id: `Cell_1`,
              type: qualify(Plugins.name('Layout'), Component.name('Cell')),
              data: ERecord({
                children: EList([]),
              }),
            }),
            EElement({
              id: `Cell_2`,
              type: qualify(Plugins.name('Layout'), Component.name('Cell')),
              data: ERecord({
                children: EList([]),
              }),
            }),
          ]),
        }),
      }),
      helpData: {
        videoSrc:
          'https://d3e54v103j8qbb.cloudfront.net/videos/tooltips/d2/quick-stack-modal.bd6e18f9a3.mp4',
        description: [
          'Use the Quick Stack element to create and control layouts, based on CSS grid. You can also make Quick Stack responsive across device types.',
        ],
        tip: 'By default, the Quick Stack element spans the width of the browser window. For more control, place it inside a Container element instead.',
      },
    }),
    Grid: ElementPreset.create({
      addTabIndex: 3,
      category: null,
      icon: {
        small: qualify(Plugins.name('Layout'), iconName('DisplayGrid')),
        medium: qualify(Plugins.name('Layout'), iconName('Grid')),
      },
      label: 'Grid',
      factory: EElement({
        id: 'Grid',
        type: qualify(Plugins.name('Layout'), Component.name('Grid')),
        data: ERecord({
          tag: EEnum('div'),
          grid: EText('two-by-two'),
          children: EList([]),
        }),
      }),
      helpData: {
        description: [
          'Use the Grid element to create and control layouts — horizontally and vertically — based on CSS grid. Customize a Grid from the Style panel on the right, and make your Grid responsive across device types.',
        ],
        videoSrc:
          'https://d3e54v103j8qbb.cloudfront.net/videos/tooltips/d2/grid.e24d25305d.mp4',
      },
    }),
    Row: ElementPreset.create({
      addTabIndex: 4,
      category: null,
      icon: {
        small: qualify(Plugins.name('Layout'), iconName('Row')),
        medium: qualify(Plugins.name('Layout'), iconName('AddPanelRow')),
      },
      label: 'Columns',
      factory: EElement({
        id: 'Row',
        type: qualify(Plugins.name('Layout'), Component.name('Row')),
        data: ERecord({
          grid: ERecord({
            type: EText('row'),
            cols: ERecord({
              main: EText('6|6'),
              medium: EText(''),
              small: EText(''),
              tiny: EText(''),
            }),
          }),
          temp: ERecord({
            grid: ERecord({
              cols: ERecord({
                main: EText(''),
                medium: EText(''),
                small: EText(''),
                tiny: EText(''),
              }),
            }),
          }),
          tag: EEnum('div'),
          children: EList([
            EElement({
              id: 'Column_1',
              type: qualify(Plugins.name('Layout'), Component.name('Column')),
              data: ERecord({
                grid: ERecord({
                  type: EText('col'),
                }),
                children: EList([]),
                tag: EEnum('div'),
              }),
            }),
            EElement({
              id: 'Column_2',
              type: qualify(Plugins.name('Layout'), Component.name('Column')),
              data: ERecord({
                grid: ERecord({
                  type: EText('col'),
                }),
                children: EList([]),
                tag: EEnum('div'),
              }),
            }),
          ]),
        }),
      }),
      helpData: {
        description: [
          'Add side-by-side columns to your site and customize their width as you go. You can also stack your columns on smartphone or tablet breakpoints to optimize for smaller devices.',
        ],
        tip: 'By default, columns span the width of the browser window. For more control, place them inside a Container element instead.',
        videoSrc:
          'https://d3e54v103j8qbb.cloudfront.net/videos/tooltips/d2/columns.2188959e8a.mp4',
      },
    }),
    VFlex: ElementPreset.create({
      addTabIndex: 4,
      category: null,
      icon: {
        small: qualify(Plugins.name('Layout'), iconName('VFlex')),
        medium: qualify(Plugins.name('Layout'), iconName('VFlex')),
      },
      label: 'V Flex',
      factory: EElement({
        id: 'VFlex',
        type: qualify(Plugins.name('Layout'), Component.name('VFlex')),
        data: ERecord({
          children: EList([]),
          tag: EEnum('div'),
        }),
      }),
      helpData: {
        tag: 'div',
        description: [
          'Use V Flex to align content vertically within a layout. V Flex is a Div block set to Flexbox by default with direction set to vertical.',
        ],
        videoSrc:
          'https://d3e54v103j8qbb.cloudfront.net/videos/tooltips/d2/vflex.e849c466bc.mp4',
      },
    }),
    HFlex: ElementPreset.create({
      addTabIndex: 5,
      category: null,
      icon: {
        small: qualify(Plugins.name('Layout'), iconName('HFlex')),
        medium: qualify(Plugins.name('Layout'), iconName('HFlex')),
      },
      label: 'H Flex',
      factory: EElement({
        id: 'HFlex',
        type: qualify(Plugins.name('Layout'), Component.name('HFlex')),
        data: ERecord({
          children: EList([]),
          tag: EEnum('div'),
        }),
      }),
      helpData: {
        tag: 'div',
        description: [
          'Use H Flex to align content horizontally within a layout. H Flex is a Div block set to Flexbox by default with direction set to horizontal.',
        ],
        videoSrc:
          'https://d3e54v103j8qbb.cloudfront.net/videos/tooltips/d2/hflex.4c483d9a24.mp4',
      },
    }),
  },
});
